// <copyright file="assetMarkerIcons.ts" company="SureView Systems">
//     Copyright (c) SureView Systems. All rights reserved.
// </copyright>

// Turns the Asset Type marker icon preference into entries the picker can show, and resolves a
// Font Awesome class to the SVG path data the hand-built map pin needs — the pin is drawn as raw
// SVG and so cannot use the webfont.
//
// ADR: docs/adrs/20260731-121505-custom-asset-marker-icon.md in the SureView.SpecKit.SaaS repo.

import { IconDefinition } from "@fortawesome/fontawesome-common-types";

export interface AssetMarkerIcon {
	/** Font Awesome CSS class stored on AssetType.customIcon, e.g. "fas fa-hospital". */
	cssClass: string;
	/** Human label shown in the picker, e.g. "Hospital". */
	title: string;
}

export interface AssetMarkerIconCategory {
	/** Category heading shown in the picker, e.g. "Surveillance". */
	title: string;
	/** The selectable icons under this category. */
	icons: AssetMarkerIcon[];
}

// A well-formed Font Awesome class: a style prefix plus a "fa-" icon name.
const FA_CLASS_PATTERN = /^(fas|far|fal|fad|fab) fa-[a-z0-9-]{1,40}$/;

// Heading used for icons that have no category (flat CSV value, or a category with a blank title).
export const DEFAULT_CATEGORY_TITLE = "General";

// Not an arbitrary UI cap: PrefGet reads the preference through a VARCHAR(2500) local, so a longer
// value comes back truncated and every icon past the cut is lost. Must match
// AssetIconConfigMaxLength in OrganizationSettingsController.
export const ICON_CONFIG_MAX_LENGTH = 2500;

// Warn the admin they are running out of room before they hit the wall.
export const ICON_CONFIG_NEARLY_FULL_LENGTH = Math.floor(ICON_CONFIG_MAX_LENGTH * 0.9);

/** True when the value is a well-formed Font Awesome class, e.g. "fas fa-user-shield". */
export function isValidIconClass(cssClass: string | null | undefined): boolean {
	return FA_CLASS_PATTERN.test((cssClass || "").trim());
}

/**
 * Parse a comma-separated class list into picker entries, de-duplicated and in the order given.
 * Anything that is not a well-formed FA class is dropped rather than rendered as a broken option.
 */
export function parseIconClasses(csv: string | null | undefined): AssetMarkerIcon[] {
	if (!csv) {
		return [];
	}

	const seen = new Set<string>();
	const entries: AssetMarkerIcon[] = [];

	for (const raw of csv.split(",")) {
		const cssClass = raw.trim();
		if (!cssClass || seen.has(cssClass) || !FA_CLASS_PATTERN.test(cssClass)) {
			continue;
		}
		seen.add(cssClass);
		entries.push({ cssClass, title: titleFromClass(cssClass) });
	}

	return entries;
}

// Tolerates two ways the preference comes back from storage: escaped quotes/backslashes, and a
// value that is itself a JSON-encoded string. Undefined when it is not JSON at all.
function tryParseJson(value: string): any {
	const candidates = [value, value.replace(/\\"/g, '"').replace(/\\\\/g, "\\")];
	for (const candidate of candidates) {
		try {
			let result = JSON.parse(candidate);
			if (typeof result === "string") {
				try {
					result = JSON.parse(result);
				} catch {
					/* keep the string result */
				}
			}
			return result;
		} catch {
			/* try the next candidate */
		}
	}
	return undefined;
}

function mapRawCategories(arr: any[]): AssetMarkerIconCategory[] {
	return arr
		.map(entry => ({
			title: (entry && typeof entry.category_title === "string" && entry.category_title.trim())
				? entry.category_title
				: DEFAULT_CATEGORY_TITLE,
			icons: parseIconClasses(entry && entry.fa_icons),
		}))
		.filter(category => category.icons.length > 0);
}

/**
 * Parse the icon preference into picker categories. The value is a JSON array of
 * { category_title, fa_icons } objects; a plain comma-separated class list is also accepted and
 * read as a single "General" group, which is how the preference was stored before categories.
 */
export function parseIconCategories(value: string | null | undefined): AssetMarkerIconCategory[] {
	if (!value) {
		return [];
	}

	const trimmed = value.trim();
	const looksLikeJson = trimmed.startsWith("[") || trimmed.startsWith("{") || trimmed.startsWith("\"");
	const parsed = tryParseJson(trimmed);

	if (parsed === undefined) {
		// Don't try to salvage classes out of JSON that wouldn't parse — a partial list looks like
		// a working configuration. Returning nothing makes the misconfiguration visible instead.
		if (looksLikeJson) {
			console.warn("Asset icon configuration looks like JSON but could not be parsed; ignoring it.");
			return [];
		}
		const icons = parseIconClasses(value);
		return icons.length ? [{ title: DEFAULT_CATEGORY_TITLE, icons }] : [];
	}

	if (!Array.isArray(parsed)) {
		console.warn("Asset icon configuration parsed but is not an array of categories; ignoring it.");
		return [];
	}

	return mapRawCategories(parsed);
}

/**
 * True when a stored configuration has content but cannot be read as a category list. Distinct
 * from empty, which legitimately means "this organization has no set of its own, use the default".
 *
 * Only the icon manager acts on this — it has an administrator who can fix the value, so it says
 * so rather than presenting the defaults as if they were the saved set. Maps just fall back.
 */
export function isUnreadableIconConfig(value: string | null | undefined): boolean {
	if (!value || !value.trim()) {
		return false;
	}

	const trimmed = value.trim();
	const parsed = tryParseJson(trimmed);

	if (parsed === undefined) {
		return trimmed.startsWith("[") || trimmed.startsWith("{") || trimmed.startsWith("\"");
	}

	return !Array.isArray(parsed);
}

/** The organization's own categories when it has any, otherwise the system default set. */
export function resolveIconCategories(
	tenantValue: string | null | undefined,
	defaultValue: string | null | undefined
): AssetMarkerIconCategory[] {
	const categories = parseIconCategories(tenantValue);
	return categories.length ? categories : parseIconCategories(defaultValue);
}

/**
 * The classes available to pick and render: the organization's own set when the TenantIconSet
 * sub-feature is on, the system default set when it is off. Empty when nothing has loaded yet —
 * callers read empty as "don't restrict", so an unloaded preference never blanks existing icons.
 */
export function resolveAvailableIconClasses(
	tenantSetEnabled: boolean,
	tenantValue: string | null | undefined,
	defaultValue: string | null | undefined
): Set<string> {
	const categories = tenantSetEnabled
		? resolveIconCategories(tenantValue, defaultValue)
		: parseIconCategories(defaultValue);

	const classes = new Set<string>();
	for (const category of categories) {
		for (const icon of category.icons) {
			classes.add(icon.cssClass);
		}
	}
	return classes;
}

/**
 * The icon class for the event-sharing and list badges: the Asset Type's custom class when the
 * feature is on and a valid one is set, otherwise the supplied default. Badges gate on the feature
 * flag alone; map markers additionally enforce the available set (see FieldOpsMixin).
 */
export function markerIconClassOrDefault(
	customIcon: string | null | undefined,
	fallback: string,
	featureEnabled: boolean
): string {
	return featureEnabled && isValidIconClass(customIcon) ? (customIcon as string).trim() : fallback;
}

/** "fas fa-square-parking" -> "Square Parking". */
export function titleFromClass(cssClass: string): string {
	const iconName = cssClass.split(" ")[1]?.replace(/^fa-/, "") ?? "";
	return iconName
		.split("-")
		.filter(Boolean)
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

/** "fas fa-square-parking" -> "faSquareParking" (the pro-solid-svg-icons export key). */
function iconExportName(cssClass: string): string | null {
	const iconName = cssClass.split(" ")[1]?.replace(/^fa-/, "");
	if (!iconName) {
		return null;
	}
	const camel = iconName
		.split("-")
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join("");
	return `fa${camel}`;
}

let solidPack: Record<string, IconDefinition> | null = null;
let loadPromise: Promise<void> | null = null;

/**
 * Load the solid icon pack once. The dynamic import keeps the multi-MB aggregate out of the main
 * bundle, so call this when the feature is active to let resolveIconDefSync return glyphs.
 */
export function ensureAssetIconsLoaded(): Promise<void> {
	if (solidPack) {
		return Promise.resolve();
	}
	if (!loadPromise) {
		loadPromise = import(/* webpackChunkName: "fa-solid-icons" */ "@fortawesome/pro-solid-svg-icons")
			.then(m => {
				solidPack = (m.fas as unknown) as Record<string, IconDefinition>;
			});
	}
	return loadPromise;
}

/**
 * The icon definition for a class, if the solid pack has loaded (see ensureAssetIconsLoaded).
 * Undefined for an unloaded, unknown or malformed class, which leaves the caller on the default
 * marker. Only solid ("fas") classes resolve to pin path data.
 */
export function resolveIconDefSync(cssClass?: string | null): IconDefinition | undefined {
	if (!solidPack || !cssClass || !FA_CLASS_PATTERN.test(cssClass)) {
		return undefined;
	}
	const exportName = iconExportName(cssClass);
	if (!exportName) {
		return undefined;
	}
	const def = solidPack[exportName];
	return def && Array.isArray(def.icon) ? def : undefined;
}

/** Loads the pack if needed, then resolves. For callers that can await; the map cannot. */
export async function resolveIconDef(cssClass?: string | null): Promise<IconDefinition | undefined> {
	if (!cssClass || !FA_CLASS_PATTERN.test(cssClass)) {
		return undefined;
	}
	await ensureAssetIconsLoaded();
	return resolveIconDefSync(cssClass);
}

export interface AssetMarkerSearchResult {
	cssClass: string;
	title: string;
	/** viewBox dimensions and path, so the caller can render the glyph as inline SVG. */
	width: number;
	height: number;
	path: string;
}

/**
 * Search the solid icon pack by keyword, matching the icon name hyphen- or space-separated.
 * Results carry the pack's own path data, so the search shows exactly what the map will draw.
 */
export async function searchIcons(term: string, limit: number = 60): Promise<AssetMarkerSearchResult[]> {
	const q = (term || "").trim().toLowerCase();
	if (q.length < 2) {
		return [];
	}
	await ensureAssetIconsLoaded();
	if (!solidPack) {
		return [];
	}

	const results: AssetMarkerSearchResult[] = [];
	for (const key of Object.keys(solidPack)) {
		const def = solidPack[key];
		if (!def || !def.iconName || !Array.isArray(def.icon)) {
			continue;
		}
		const name = def.iconName;
		if (name.indexOf(q) === -1 && name.replace(/-/g, " ").indexOf(q) === -1) {
			continue;
		}
		const raw = def.icon[4] as string | string[];
		const cssClass = `fas fa-${name}`;
		results.push({
			cssClass,
			title: titleFromClass(cssClass),
			width: def.icon[0] as number,
			height: def.icon[1] as number,
			path: Array.isArray(raw) ? raw[raw.length - 1] : raw,
		});
		if (results.length >= limit) {
			break;
		}
	}

	// Shorter names tend to be the closer match ("shield" before "shield-halved").
	results.sort((a, b) => a.cssClass.length - b.cssClass.length);
	return results;
}
